/**
 * Pesquisa de páginas no menu lateral.
 *
 * Lógica pura sobre a árvore devolvida por `buildMenu` (src/config/menu.js):
 * devolve uma cópia podada com os ramos que casam com o termo, para o
 * `AppSubmenu` renderizar com os grupos abertos. As funções `visible` dos
 * itens originais mantêm-se, por isso as ACLs continuam a mandar.
 */

import { isItemVisible } from "@/config/menu";

export function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

export function itemMatches(item, normalizedTerm) {
  if (!item || !normalizedTerm) {
    return false;
  }
  if (normalizeText(item.label).includes(normalizedTerm)) {
    return true;
  }
  return (item.keywords || []).some((keyword) =>
    normalizeText(keyword).includes(normalizedTerm)
  );
}

const visibleChildren = (item) =>
  (item.items || []).filter((child) => child && isItemVisible(child));

/**
 * Cópia podada da árvore. Por item visível:
 *  - o próprio casa → entra inteiro, com todos os filhos visíveis;
 *  - um descendente casa → entra só com os ramos que casam;
 *  - caso contrário → sai.
 */
export function filterMenu(items, term) {
  const normalizedTerm = normalizeText(term);
  if (!normalizedTerm) {
    return items || [];
  }

  const prune = (list) =>
    (list || []).reduce((kept, item) => {
      if (!item || item.separator || !isItemVisible(item)) {
        return kept;
      }

      if (itemMatches(item, normalizedTerm)) {
        kept.push(
          item.items ? { ...item, items: visibleChildren(item) } : item
        );
        return kept;
      }

      if (item.items) {
        const children = prune(item.items);
        if (children.length > 0) {
          kept.push({ ...item, items: children });
        }
      }

      return kept;
    }, []);

  return prune(items);
}

/** Primeira folha (item com destino) da árvore, em profundidade. */
export function firstLeaf(items) {
  for (const item of items || []) {
    if (!item || item.separator) {
      continue;
    }
    if (item.items) {
      const leaf = firstLeaf(item.items);
      if (leaf) {
        return leaf;
      }
      continue;
    }
    if (item.to || item.url) {
      return item;
    }
  }
  return null;
}
